/**
 * Linking a pharmacy dispense transaction back to the CPOE order it fills.
 *
 * There is no foreign key to use. `source_reference_id` is only sent when the
 * prescription id is a uuid, and IPD order ids never are, so the surviving link is
 * the remarks string StartDispensePage writes: "Dispense for ORD-…". Fragile, but it
 * is what exists — see buildCreateDispenseBody for why transaction_number cannot be
 * used instead (it is server-generated as DISP-…, deliberately, to avoid 409s).
 *
 * Import-free so the matching logic stays unit-testable (same reasoning as
 * prescriptionStatus / prescriptionMedicine: anything reaching api/inventory-request
 * pulls in axios ESM, which Jest cannot load).
 */

/** Statuses that mean the pharmacy is done with the transaction. */
const CLOSED_DISPENSE_STATUSES = ['DISPENSED', 'COMPLETED', 'CANCELLED', 'REVERSED'];

/** The CPOE order a dispense was created for, or '' when it cannot be resolved. */
export function dispenseOrderNo(dispense) {
  if (!dispense || typeof dispense !== 'object') return '';
  const fromRemarks = String(dispense.remarks || '').match(/Dispense for\s+(\S+)/i);
  if (fromRemarks?.[1]) return fromRemarks[1];
  return dispense.transaction_number ? String(dispense.transaction_number) : '';
}

export function dispenseId(dispense) {
  return dispense?.transaction_id || dispense?.id || '';
}

/** Still in flight — this is the transaction a pharmacist should continue. */
export function isOpenDispense(dispense) {
  const status = String(dispense?.status || '').toUpperCase();
  if (!status) return false;
  return !CLOSED_DISPENSE_STATUSES.includes(status);
}

/** Newest-first ordering key. Falls back to the date-only field when created_at is absent. */
function dispenseSortKey(dispense) {
  return String(dispense?.created_at || dispense?.transaction_date || '');
}

/**
 * order_no -> the single open dispense to resume.
 *
 * An order can own several. "Continue Dispense" used to reopen the *start* page,
 * which created a brand-new transaction (and a brand-new controlled-auth request)
 * every pass — so a pharmacist who round-tripped through the Controlled Queue left a
 * trail of abandoned transactions. The newest open one is the live attempt; the rest
 * are the earlier laps.
 */
export function openDispensesByOrder(list) {
  const byOrder = new Map();
  (Array.isArray(list) ? list : []).forEach((dispense) => {
    const orderNo = dispenseOrderNo(dispense);
    if (!orderNo || !isOpenDispense(dispense)) return;
    const current = byOrder.get(orderNo);
    if (!current || dispenseSortKey(dispense) >= dispenseSortKey(current)) {
      byOrder.set(orderNo, dispense);
    }
  });
  return byOrder;
}

/**
 * The dispense list with the loop's duplicates collapsed away.
 *
 * Only *open* transactions that share an order are collapsed, and only down to the
 * newest — a closed one is a real record of work that happened, and a row with no
 * resolvable order (direct dispenses, walk-ins) is not a duplicate of anything.
 *
 * Nothing is deleted: the hidden transactions still exist and are still reachable at
 * /pharmacy/dispenses/<id>. This only stops the list repeating one order N times.
 */
export function dedupeDispensesByOrder(list) {
  const rows = Array.isArray(list) ? list : [];
  const keepByOrder = openDispensesByOrder(rows);
  const keptIds = new Set();
  keepByOrder.forEach((dispense) => keptIds.add(String(dispenseId(dispense))));

  return rows.filter((dispense) => {
    const orderNo = dispenseOrderNo(dispense);
    if (!orderNo || !isOpenDispense(dispense)) return true;
    return keptIds.has(String(dispenseId(dispense)));
  });
}

/** How many rows dedupeDispensesByOrder would hide — for an honest "N hidden" note. */
export function duplicateDispenseCount(list) {
  const rows = Array.isArray(list) ? list : [];
  return rows.length - dedupeDispensesByOrder(rows).length;
}
